/**
 * AppearanceSettingsContext - Manages appearance defaults state and persistence
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { api } from '../lib/api';
import { showConfirm } from '../components/common/AppDialogs';

// Default configurations for tile types
const defaultTileConfigs = {
  content: {
    backgroundColor: '#ffffff',
    gradientColor: '',
    gradientDirection: 'to-br',
    textColor: '#111827',
    fontSize: '14px',
    fontWeight: 'normal',
    fontStyle: 'normal',
    textDecoration: 'none',
    textAlign: 'left',
    descriptionTextColor: '#6b7280',
    descriptionFontSize: '12px',
    descriptionFontWeight: 'normal',
    descriptionFontStyle: 'normal',
    descriptionTextDecoration: 'none',
    descriptionTextAlign: 'left',
    headerAlignment: 'left',
    borderColor: '#e5e7eb',
    borderThickness: '1',
    dividerColor: '#e5e7eb',
    dividerThickness: '1px',
    shape: 'rectangle',
    backgroundImage: null,
    backgroundImageFit: 'fill',
    restrictDelete: false
  },
  form: {
    backgroundColor: '#f0f9ff',
    gradientColor: '',
    gradientDirection: 'to-br',
    textColor: '#1e40af',
    fontSize: '14px',
    fontWeight: 'normal',
    fontStyle: 'normal',
    textDecoration: 'none',
    textAlign: 'left',
    descriptionTextColor: '#6b7280',
    descriptionFontSize: '12px',
    descriptionFontWeight: 'normal',
    descriptionFontStyle: 'normal',
    descriptionTextDecoration: 'none',
    descriptionTextAlign: 'left',
    headerAlignment: 'left',
    borderColor: '#e5e7eb',
    borderThickness: '1',
    dividerColor: '#e5e7eb',
    dividerThickness: '1px',
    shape: 'rectangle',
    backgroundImage: null,
    backgroundImageFit: 'fill',
    restrictDelete: false
  },
  external_link: {
    backgroundColor: '#f0fdf4',
    gradientColor: '',
    gradientDirection: 'to-br',
    textColor: '#166534',
    fontSize: '14px',
    fontWeight: 'normal',
    fontStyle: 'normal',
    textDecoration: 'none',
    textAlign: 'left',
    descriptionTextColor: '#6b7280',
    descriptionFontSize: '12px',
    descriptionFontWeight: 'normal',
    descriptionFontStyle: 'normal',
    descriptionTextDecoration: 'none',
    descriptionTextAlign: 'left',
    headerAlignment: 'left',
    borderColor: '#e5e7eb',
    borderThickness: '1',
    dividerColor: '#e5e7eb',
    dividerThickness: '1px',
    shape: 'rectangle',
    backgroundImage: null,
    backgroundImageFit: 'fill',
    restrictDelete: false
  },
  shortcut: {
    backgroundColor: '#fefce8',
    gradientColor: '',
    gradientDirection: 'to-br',
    textColor: '#a16207',
    fontSize: '14px',
    fontWeight: 'normal',
    fontStyle: 'normal',
    textDecoration: 'none',
    textAlign: 'left',
    descriptionTextColor: '#6b7280',
    descriptionFontSize: '12px',
    descriptionFontWeight: 'normal',
    descriptionFontStyle: 'normal',
    descriptionTextDecoration: 'none',
    descriptionTextAlign: 'left',
    headerAlignment: 'left',
    borderColor: '#e5e7eb',
    borderThickness: '1',
    dividerColor: '#e5e7eb',
    dividerThickness: '1px',
    shape: 'rectangle',
    backgroundImage: null,
    backgroundImageFit: 'fill',
    restrictDelete: false
  },
  nested_page: {
    backgroundColor: '#fef7ff',
    gradientColor: '',
    gradientDirection: 'to-br',
    textColor: '#86198f',
    fontSize: '14px',
    fontWeight: 'normal',
    fontStyle: 'normal',
    textDecoration: 'none',
    textAlign: 'left',
    descriptionTextColor: '#6b7280',
    descriptionFontSize: '12px',
    descriptionFontWeight: 'normal',
    descriptionFontStyle: 'normal',
    descriptionTextDecoration: 'none',
    descriptionTextAlign: 'left',
    headerAlignment: 'left',
    borderColor: '#e5e7eb',
    borderThickness: '1',
    dividerColor: '#e5e7eb',
    dividerThickness: '1px',
    shape: 'rectangle',
    backgroundImage: null,
    backgroundImageFit: 'fill',
    restrictDelete: false
  },
  roadmap: {
    // Base config (applies to all states unless overridden)
    // NOTE: No top-level backgroundColor - roadmap tiles use state-specific colors only
    gradientColor: '',
    gradientDirection: 'to-br',
    textColor: '#111827',
    fontSize: '14px',
    fontWeight: 'normal',
    fontStyle: 'normal',
    textDecoration: 'none',
    textAlign: 'left',
    descriptionTextColor: '#6b7280',
    descriptionFontSize: '12px',
    descriptionFontWeight: 'normal',
    descriptionFontStyle: 'normal',
    descriptionTextDecoration: 'none',
    descriptionTextAlign: 'left',
    headerAlignment: 'left',
    borderColor: '#e5e7eb',
    borderThickness: '1',
    dividerColor: '#e5e7eb',
    dividerThickness: '1px',
    shape: 'rectangle',
    backgroundImage: null,
    backgroundImageFit: 'fill',
    restrictDelete: false,
    
    // State-specific configurations
    todoState: {
      backgroundColor: '#f9fafb', // light gray
      borderColor: '#e5e7eb',
      borderThickness: '1px'
    },
    inProgressState: {
      backgroundColor: '#dcfce7', // light green
      textColor: '#166534', // dark green text
      fontWeight: 'medium',
      borderColor: '#22c55e',
      borderThickness: '2px'
    },
    completeState: {
      backgroundColor: '#f3f4f6', // gray
      textColor: '#6b7280', // muted text
      borderColor: '#9ca3af',
      borderThickness: '1px'
    }
  }
};

const defaultHeaderConfigs = {
  row: {
    backgroundColor: '#f3f4f6',
    gradientColor: '',
    gradientDirection: 'to-br',
    textColor: '#374151',
    fontSize: '14px',
    fontWeight: 'medium',
    fontStyle: 'normal',
    textDecoration: 'none',
    textAlign: 'center',
    alignItems: 'center',
    outlineColor: '#e5e7eb',
    outlineThickness: '1px',
    applyBorderToBackground: false
  },
  column: {
    backgroundColor: '#f3f4f6',
    gradientColor: '',
    gradientDirection: 'to-br',
    textColor: '#374151',
    fontSize: '14px',
    fontWeight: 'medium',
    fontStyle: 'normal',
    textDecoration: 'none',
    textAlign: 'center',
    outlineColor: '#e5e7eb',
    outlineThickness: '1px',
    applyBorderToBackground: false
  }
};

interface AppearanceSettingsState {
  tileConfigs: Record<string, any>;
  headerConfigs: { row: any; column: any };
  selectedTile: string | null;
  selectedHeader: 'row' | 'column' | null;
  selectedRoadmapState: 'todo' | 'in_progress' | 'complete';
  hasUnsavedChanges: boolean;
  isLoading: boolean;
  isDarkMode: boolean;
  defaultGridBackgroundColor: string;
  isInitializing: boolean;
  isApplyingDefaults: boolean; // Flag to prevent resets during multi-tile operations
}

interface AppearanceSettingsContextType extends AppearanceSettingsState {
  updateTileConfig: (tileId: string, updates: any) => void;
  updateHeaderConfig: (headerType: 'row' | 'column', updates: any) => void;
  setSelectedTile: (tileId: string | null) => void;
  setSelectedHeader: (headerType: 'row' | 'column' | null) => void;
  setSelectedRoadmapState: (state: 'todo' | 'in_progress' | 'complete' | ((prev: 'todo' | 'in_progress' | 'complete') => 'todo' | 'in_progress' | 'complete')) => void;
  saveSettings: () => Promise<void>;
  loadSettings: () => Promise<void>;
  resetToDefaults: () => void;
  organizationId: string | null;
  setOrganizationId: (orgId: string) => void;
  toggleDarkMode: () => void;
  updateDefaultGridBackgroundColor: (color: string) => void;
  setIsApplyingDefaults: (isApplying: boolean) => void;
}

const AppearanceSettingsContext = createContext<AppearanceSettingsContextType | null>(null);

export function AppearanceSettingsProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AppearanceSettingsState>({
    tileConfigs: { ...defaultTileConfigs },
    headerConfigs: { ...defaultHeaderConfigs },
    selectedTile: null,
    selectedHeader: null,
    selectedRoadmapState: 'todo',
    hasUnsavedChanges: false,
    isLoading: false,
    isDarkMode: false,
    defaultGridBackgroundColor: '#f9fafb',
    isInitializing: true,
    isApplyingDefaults: false
  });

  // Debug: Log context initialization (reduced logging)
  // console.log('🔧 Context initialized with roadmap config:', defaultTileConfigs.roadmap);

  const [organizationId, setOrganizationId] = useState<string | null>(null);

  // Apply dark mode to document
  React.useEffect(() => {
    if (state.isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [state.isDarkMode]);

  // Fetch user organization ID when context initializes
  useEffect(() => {
    // This will be handled by React Query hook below
  }, []); // Run once on mount
  
  // Use React Query to get user data (same as other components) - reduces redundant API calls
  useQuery(
    ['current-user'],
    () => api.get('/users/me').then(res => res.data.data),
    { 
      staleTime: 5 * 60 * 1000,
      onSuccess: (data) => {
        if (data?.profile?.organizationId) {
          setOrganizationId(data.profile.organizationId);
        } else {
          console.log('⚠️ AppearanceSettingsContext - No organization ID found in user profile, using fallback');
          setOrganizationId('nexus'); // Fallback to default
        }
      },
      onError: (error) => {
        console.error('❌ AppearanceSettingsContext - Failed to fetch user organization:', error);
        console.log('🔄 AppearanceSettingsContext - Using fallback organization ID: nexus');
        setOrganizationId('nexus'); // Fallback to default on error
      }
    }
  );

  // Migrate old flat roadmap structure to new nested structure
  const migrateRoadmapConfig = (config: any) => {
    if (!config || config.todoState || config.inProgressState || config.completeState) {
      // Already has nested structure or is null/undefined
      return config;
    }

    console.log('🔄 Migrating old roadmap structure to nested format');
    
    // Extract old flat properties
    const {
      todoBackgroundColor,
      inProgressBackgroundColor,
      completeBackgroundColor,
      ...baseConfig
    } = config;

    // Create new nested structure
    const migratedConfig = {
      ...baseConfig,
      todoState: {
        ...(todoBackgroundColor && { backgroundColor: todoBackgroundColor })
      },
      inProgressState: {
        ...(inProgressBackgroundColor && { backgroundColor: inProgressBackgroundColor })
      },
      completeState: {
        ...(completeBackgroundColor && { backgroundColor: completeBackgroundColor })
      }
    };

    console.log('🔄 Migrated roadmap config:', migratedConfig);
    return migratedConfig;
  };

  // Load settings from API when organizationId is set
  const loadSettings = async () => {
    if (!organizationId) return;

    // Don't reload if we're currently applying defaults to prevent race conditions
    if (state.isApplyingDefaults) {
      console.log('⚠️ Skipping settings reload - currently applying defaults');
      return;
    }

    setState(prev => ({ ...prev, isLoading: true }));

    try {

      
      // Load organization settings and user settings in parallel
      const [orgResponse, userResponse] = await Promise.allSettled([
        api.get(`/settings/organization/${organizationId}/appearance_defaults`).catch(err => {
          if (err.response?.status === 404) {
            console.log('📋 No organization appearance defaults found (this is normal for new organizations)');
          }
          throw err;
        }),
        api.get(`/settings/user/appearance_preferences`).catch(err => {
          if (err.response?.status === 404) {
            console.log('👤 No user appearance preferences found (this is normal for new users)');
          }
          throw err;
        })
      ]);

      // Extract organization settings
      const savedOrgSettings = orgResponse.status === 'fulfilled' ? orgResponse.value.data.settingValue : {};
      
      // Extract user settings
      const savedUserSettings = userResponse.status === 'fulfilled' ? userResponse.value.data.settingValue : {};



      // Migrate roadmap config if needed
      const migratedTileConfigs = {
        ...savedOrgSettings?.tileConfigs,
        ...(savedOrgSettings?.tileConfigs?.roadmap && {
          roadmap: migrateRoadmapConfig(savedOrgSettings.tileConfigs.roadmap)
        })
      };

      const mergedConfigs = {
        tileConfigs: { ...defaultTileConfigs, ...migratedTileConfigs },
        headerConfigs: { ...defaultHeaderConfigs, ...savedOrgSettings?.headerConfigs },
        isDarkMode: savedUserSettings?.isDarkMode || false,
        defaultGridBackgroundColor: savedOrgSettings?.defaultGridBackgroundColor || '#f9fafb'
      };


      


      setState(prev => ({
        ...prev,
        tileConfigs: mergedConfigs.tileConfigs,
        headerConfigs: mergedConfigs.headerConfigs,
        isDarkMode: mergedConfigs.isDarkMode,
        defaultGridBackgroundColor: mergedConfigs.defaultGridBackgroundColor,
        hasUnsavedChanges: false,
        isLoading: false,
        isInitializing: false
      }));
    } catch (error) {
      console.error('❌ Error loading appearance settings:', error);
      
      // Only reset to defaults if this is the first load (isInitializing is true)
      // If we're reloading due to navigation, preserve existing settings
      setState(prev => {
        if (prev.isInitializing) {
          // First load - use defaults
          console.log('⚠️ First load: Using default appearance settings');
          return {
            ...prev,
            tileConfigs: { ...defaultTileConfigs },
            headerConfigs: { ...defaultHeaderConfigs },
            isDarkMode: false,
            defaultGridBackgroundColor: '#f9fafb',
            hasUnsavedChanges: false,
            isLoading: false,
            isInitializing: false
          };
        } else {
          // Reload due to navigation - preserve existing settings
          console.log('⚠️ Reload failed: Preserving existing appearance settings');
          return {
            ...prev,
            isLoading: false,
            isInitializing: false
          };
        }
      });
    }
  };

  // Save settings to API
  const saveSettings = async () => {
    if (!organizationId) return;

    setState(prev => ({ ...prev, isLoading: true }));

    try {
      // Organization settings (shared across all users in the org)
      const organizationSettingValue = {
        tileConfigs: state.tileConfigs,
        headerConfigs: state.headerConfigs,
        defaultGridBackgroundColor: state.defaultGridBackgroundColor
      };

      // User settings (personal preferences)
      const userSettingValue = {
        isDarkMode: state.isDarkMode
      };
      

      

      

      
      // Save organization settings and user settings in parallel
      await Promise.all([
        api.put(`/settings/organization/${organizationId}/appearance_defaults`, {
          settingValue: organizationSettingValue
        }),
        api.put(`/settings/user/appearance_preferences`, {
          settingValue: userSettingValue
        })
      ]);
      


      setState(prev => ({
        ...prev,
        hasUnsavedChanges: false,
        isLoading: false
      }));
    } catch (error) {
      console.error('❌ Failed to save appearance settings:', error);
      setState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  const updateTileConfig = (tileId: string, updates: any) => {
    console.log('🔄 Context updateTileConfig called:', {
      tileId,
      updates,
      currentTileConfig: state.tileConfigs[tileId],
      willBecome: { ...state.tileConfigs[tileId], ...updates }
    });
    
    // 📝 DEBUGGING: Special focus on roadmap tile updates

    
    setState(prev => {
      const currentConfig = prev.tileConfigs[tileId] || {};
      
      // For roadmap tiles, we need to be careful about nested state updates
      if (tileId === 'roadmap') {
        
        // Deep merge to preserve nested state structures
        const newConfig = { ...currentConfig };
        
        // Handle nested state updates
        if (updates.todoState || updates.inProgressState || updates.completeState) {
          if (updates.todoState) {
            newConfig.todoState = { ...currentConfig.todoState, ...updates.todoState };
          }
          if (updates.inProgressState) {
            newConfig.inProgressState = { ...currentConfig.inProgressState, ...updates.inProgressState };
          }
          if (updates.completeState) {
            newConfig.completeState = { ...currentConfig.completeState, ...updates.completeState };
          }
          
          // Remove the nested state objects from the top-level updates to prevent shallow merge issues
          const { todoState, inProgressState, completeState, ...baseUpdates } = updates;
          
          // Apply base-level updates
          Object.assign(newConfig, baseUpdates);
        } else {
          // Simple shallow merge for non-nested updates
          Object.assign(newConfig, updates);
        }
        

        
        return {
          ...prev,
          tileConfigs: {
            ...prev.tileConfigs,
            [tileId]: newConfig
          },
          hasUnsavedChanges: prev.isInitializing ? false : true
        };
      } else {
        // For non-roadmap tiles, use simple shallow merge
        return {
          ...prev,
          tileConfigs: {
            ...prev.tileConfigs,
            [tileId]: {
              ...currentConfig,
              ...updates
            }
          },
          hasUnsavedChanges: prev.isInitializing ? false : true
        };
      }
    });
  };

  const updateHeaderConfig = (headerType: 'row' | 'column', updates: any) => {
    setState(prev => ({
      ...prev,
      headerConfigs: {
        ...prev.headerConfigs,
        [headerType]: {
          ...prev.headerConfigs[headerType],
          ...updates
        }
      },
      hasUnsavedChanges: prev.isInitializing ? false : true
    }));
  };

  const setSelectedTile = (tileId: string | null) => {
    setState(prev => ({
      ...prev,
      selectedTile: tileId,
      selectedHeader: null // Clear header selection when selecting tile
    }));
  };

  const setSelectedHeader = (headerType: 'row' | 'column' | null) => {
    setState(prev => ({
      ...prev,
      selectedHeader: headerType,
      selectedTile: null // Clear tile selection when selecting header
    }));
  };

  const setSelectedRoadmapState = (roadmapState: 'todo' | 'in_progress' | 'complete' | ((prev: 'todo' | 'in_progress' | 'complete') => 'todo' | 'in_progress' | 'complete')) => {
    setState(prev => ({ 
      ...prev, 
      selectedRoadmapState: typeof roadmapState === 'function' ? roadmapState(prev.selectedRoadmapState) : roadmapState 
    }));
  };

  const resetToDefaults = async () => {
    // Add confirmation dialog to prevent accidental resets
    const confirmed = await showConfirm(
      'Are you sure you want to reset all appearance defaults to their original values? This action cannot be undone.'
    );
    
    if (!confirmed) {
      return;
    }

    setState(prev => ({
      ...prev,
      tileConfigs: { ...defaultTileConfigs },
      headerConfigs: { ...defaultHeaderConfigs },
      isDarkMode: false,
      defaultGridBackgroundColor: '#f9fafb',
      hasUnsavedChanges: prev.isInitializing ? false : true
    }));
  };

  const toggleDarkMode = () => {
    setState(prev => ({
      ...prev,
      isDarkMode: !prev.isDarkMode,
      hasUnsavedChanges: prev.isInitializing ? false : true
    }));
  };

  const updateDefaultGridBackgroundColor = (color: string) => {
    setState(prev => ({
      ...prev,
      defaultGridBackgroundColor: color,
      hasUnsavedChanges: prev.isInitializing ? false : true
    }));
  };

  const setIsApplyingDefaults = (isApplying: boolean) => {
    setState(prev => ({
      ...prev,
      isApplyingDefaults: isApplying
    }));
  };

  // Load settings when organizationId changes
  useEffect(() => {
    if (organizationId) {
      loadSettings();
    }
  }, [organizationId]);

  const contextValue: AppearanceSettingsContextType = {
    ...state,
    updateTileConfig,
    updateHeaderConfig,
    setSelectedTile,
    setSelectedHeader,
    setSelectedRoadmapState,
    saveSettings,
    loadSettings,
    resetToDefaults,
    organizationId,
    setOrganizationId,
    toggleDarkMode,
    updateDefaultGridBackgroundColor,
    setIsApplyingDefaults
  };

  return (
    <AppearanceSettingsContext.Provider value={contextValue}>
      {children}
    </AppearanceSettingsContext.Provider>
  );
}

export function useAppearanceSettings() {
  const context = useContext(AppearanceSettingsContext);
  if (!context) {
    throw new Error('useAppearanceSettings must be used within AppearanceSettingsProvider');
  }
  return context;
}

// Export defaultTileConfigs for external use
export { defaultTileConfigs };
